import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Card,
  CardContent,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Divider,
  Tooltip,
  IconButton,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  Assessment as AssessmentIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { legacyEvaluationApi } from '../../services/legacyEvaluationApi';

const LegacyEvaluations = () => {
  const [evaluations, setEvaluations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedEvaluation, setSelectedEvaluation] = useState(null);
  const [openDetail, setOpenDetail] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredEvaluations, setFilteredEvaluations] = useState([]);
  const [error, setError] = useState(null);

  // 載入舊評估記錄
  const loadLegacyEvaluations = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await legacyEvaluationApi.getLegacyEvaluations();
      setEvaluations(data.evaluations || []);
      setFilteredEvaluations(data.evaluations || []);
    } catch (error) {
      console.error('載入舊評估記錄失敗:', error);
      if (error.response?.status === 403) {
        setError('權限不足：只有管理員可以查看舊評估記錄');
      } else if (error.response?.status === 401) {
        setError('認證失敗：請重新登入');
      } else {
        setError('載入失敗：' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  // 載入統計信息
  const loadStats = async () => {
    setStatsLoading(true);
    try {
      const data = await legacyEvaluationApi.getLegacyEvaluationStats();
      setStats(data);
    } catch (error) {
      console.error('載入統計信息失敗:', error);
      if (error.response?.status === 403) {
        console.error('權限不足：只有管理員可以查看統計信息');
      } else if (error.response?.status === 401) {
        console.error('認證失敗：請重新登入');
      }
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    loadLegacyEvaluations();
    loadStats();
  }, []);

  // 搜索過濾
  useEffect(() => {
    if (searchTerm) {
      const filtered = evaluations.filter(evaluation =>
        evaluation.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        evaluation.target?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        evaluation.comments?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredEvaluations(filtered);
    } else {
      setFilteredEvaluations(evaluations);
    }
  }, [searchTerm, evaluations]);

  // 處理分頁
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // 查看詳細信息
  const handleViewDetail = (evaluation) => {
    setSelectedEvaluation(evaluation);
    setOpenDetail(true);
  };

  // 格式化日期
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('zh-TW', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // 截斷文本
  const truncateText = (text, maxLength = 50) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            舊評估記錄查看
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AssessmentIcon />}
            onClick={loadStats}
            disabled={statsLoading}
          >
            {statsLoading ? '載入中...' : '重新載入統計'}
          </Button>
        </Box>

        {/* 錯誤顯示 */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* 統計信息 */}
        {stats && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    總評估記錄
                  </Typography>
                  <Typography variant="h4">
                    {stats.total_evaluations}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    學生數量
                  </Typography>
                  <Typography variant="h4">
                    {stats.students?.length || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    教練數量
                  </Typography>
                  <Typography variant="h4">
                    {stats.coaches?.length || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    日期範圍
                  </Typography>
                  <Typography variant="body2">
                    {stats.date_range?.earliest ? formatDate(stats.date_range.earliest) : 'N/A'}
                  </Typography>
                  <Typography variant="body2">
                    至 {stats.date_range?.latest ? formatDate(stats.date_range.latest) : 'N/A'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* 搜索框 */}
        <TextField
          fullWidth
          placeholder="搜索學生姓名、目標或評語..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 3 }}
        />

        {/* 評估記錄表格 */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>學生姓名</TableCell>
                  <TableCell>目標</TableCell>
                  <TableCell>時機</TableCell>
                  <TableCell>動作</TableCell>
                  <TableCell>動作要領數量</TableCell>
                  <TableCell>評語</TableCell>
                  <TableCell>創建時間</TableCell>
                  <TableCell>教練ID</TableCell>
                  <TableCell>操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredEvaluations
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((evaluation) => (
                    <TableRow key={evaluation.id} hover>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {evaluation.student_name || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={evaluation.target || ''}>
                          <Typography variant="body2">
                            {truncateText(evaluation.target, 30)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={evaluation.timing || ''}>
                          <Typography variant="body2">
                            {truncateText(evaluation.timing, 30)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={evaluation.action || ''}>
                          <Typography variant="body2">
                            {truncateText(evaluation.action, 30)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={evaluation.action_key?.length || 0}
                          size="small"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={evaluation.comments || ''}>
                          <Typography variant="body2">
                            {truncateText(evaluation.comments, 40)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(evaluation.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={evaluation.coach_id} size="small" />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={() => handleViewDetail(evaluation)}
                          color="primary"
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredEvaluations.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        )}

        {/* 詳細信息對話框 */}
        <Dialog
          open={openDetail}
          onClose={() => setOpenDetail(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon />
              <Typography variant="h6">
                {selectedEvaluation?.student_name} - 評估詳細信息
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedEvaluation && (
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      學生姓名
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {selectedEvaluation.student_name || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      教練ID
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {selectedEvaluation.coach_id}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      目標
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {selectedEvaluation.target || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      時機
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {selectedEvaluation.timing || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      動作
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {selectedEvaluation.action || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      動作要領
                    </Typography>
                    {selectedEvaluation.action_key && selectedEvaluation.action_key.length > 0 ? (
                      <List dense>
                        {selectedEvaluation.action_key.map((key, index) => (
                          <ListItem key={index}>
                            <ListItemText primary={`${index + 1}. ${key}`} />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Typography variant="body1" color="textSecondary">
                        無動作要領
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      評語
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                      {selectedEvaluation.comments || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      創建時間
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(selectedEvaluation.created_at)}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDetail(false)}>關閉</Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Box>
  );
};

export default LegacyEvaluations;
